/* eslint-disable no-lonely-if */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import TimeRangeSelector from "components/CheckInCheckOut/TimeRangeSelector";
import EndDayModal from "components/CheckInCheckOut/EndDayModal/EndDayModal";
import TimesheetResultModal from "../TimesheetResultModal/TimesheetResultModal";
import PageLoader from "components/common/PageLoader";

import { getTimesheetDetailsById, updateTimeSheetById } from "services/CheckInCheckOut/TimesheetGetAndUpdate";
import { getEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";
import {
  getDayOfWeekFromDate,
  getScheduleForDay,
  validateClockInTime as validateClockInHelper,
  validateClockOutTime as validateClockOutHelper,
  validateBreakTimes,
  validateNursingBreakTimes,
  calculateDuration,
  calculateTotalDuration,
  getDateLabelForTime,
  timeToMinutes,
  getDateWithoutTimeZone,
  formatTimesheetDateTimeForPayload,
} from "../helpers";
import { hasPermission } from "hooks/usePermission";

import "./TimesheetDetailModal.scss";

function TimesheetDetailModalV2({
  open,
  onClose,
  rowData,
  isEditable,
  employeeId,
  onUpdateSuccess,
  employeeDetails = {},
}) {
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const isEdgeAdmin = loggedInUser?.type === "edge";
  const canAdjustTimesheet = hasPermission("timeTracking.adjust");
  const canEdit = (isEdgeAdmin || isEditable) && canAdjustTimesheet;
  const { firstName, lastName } = employeeDetails;
  const employeeName = `${firstName} ${lastName}` || loggedInUser?.name || "";

  const [loading, setLoading] = useState(false);
  const [currentDaySchedule, setCurrentDaySchedule] = useState(null);

  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [clockOutDate, setClockOutDate] = useState(null);
  const [originalClockInDate, setOriginalClockInDate] = useState(null);

  const [breakSlots, setBreakSlots] = useState([{ id: 1, startTime: null, endTime: null, date: null }]);
  const [nursingSlots, setNursingSlots] = useState([{ id: 1, startTime: null, endTime: null, date: null }]);

  const [initialClockInTime, setInitialClockInTime] = useState(null);
  const [initialClockOutTime, setInitialClockOutTime] = useState(null);
  const [initialClockOutDate, setInitialClockOutDate] = useState(null);
  const [initialBreakSlots, setInitialBreakSlots] = useState([{ id: 1, startTime: null, endTime: null, date: null }]);
  const [initialNursingSlots, setInitialNursingSlots] = useState([
    { id: 1, startTime: null, endTime: null, date: null },
  ]);

  const [showEndDayModal, setShowEndDayModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const [calculatedTimeWorked, setCalculatedTimeWorked] = useState("0 hr 0 min");
  const [calculatedBreakDuration, setCalculatedBreakDuration] = useState("0 min");
  const [calculatedNursingDuration, setCalculatedNursingDuration] = useState("0 min");

  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [breakSlotsError, setBreakSlotsError] = useState("");
  const [nursingBreakError, setNursingBreakError] = useState("");

  const formatShiftTime = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";

    try {
      const today = new Date().toISOString().split("T")[0];
      const startDateTime = new Date(`${today}T${startTime}`);
      const endDateTime = new Date(`${today}T${endTime}`);

      const formatTimeWithTimezone = (date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
      };

      return `${formatTimeWithTimezone(startDateTime)} → ${formatTimeWithTimezone(endDateTime)}`;
    } catch (error) {
      return "N/A";
    }
  };

  const parseDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;
    return new Date(dateTimeString);
  };

  const validateClockInTime = useCallback(() => {
    if (!clockInTime || !currentDaySchedule?.startTime) {
      setStartTimeError("");
      return true;
    }

    const result = validateClockInHelper(clockInTime, currentDaySchedule.startTime, currentDaySchedule.endTime);

    setStartTimeError(result.errorMessage);
    return result.isValid;
  }, [clockInTime, currentDaySchedule]);

  const validateClockOutTime = useCallback(() => {
    if (!clockOutTime) {
      setEndTimeError("");
      return true;
    }

    if (!currentDaySchedule?.endTime || !clockInTime) {
      setEndTimeError("");
      return true;
    }

    const result = validateClockOutHelper(
      clockOutTime,
      clockInTime,
      currentDaySchedule.startTime,
      currentDaySchedule.endTime
    );

    setEndTimeError(result.errorMessage);
    return result.isValid;
  }, [clockOutTime, clockInTime, currentDaySchedule]);

  const validateBreakSlots = useCallback(() => {
    if (!clockInTime) {
      setBreakSlotsError("");
      return true;
    }

    const result = validateBreakTimes(
      breakSlots,
      clockInTime,
      clockOutTime,
      currentDaySchedule?.startTime,
      currentDaySchedule?.endTime
    );

    setBreakSlotsError(result.errorMessage);
    return result.isValid;
  }, [breakSlots, clockInTime, clockOutTime, currentDaySchedule]);

  const validateNursingBreaks = useCallback(() => {
    if (!clockInTime) {
      setNursingBreakError("");
      return true;
    }

    const result = validateNursingBreakTimes(
      nursingSlots,
      clockInTime,
      clockOutTime,
      currentDaySchedule?.startTime,
      currentDaySchedule?.endTime,
      60 // max 60 minutes (1 hour)
    );

    setNursingBreakError(result.errorMessage);
    return result.isValid;
  }, [nursingSlots, clockInTime, clockOutTime, currentDaySchedule]);

  useEffect(() => {
    validateClockInTime();
  }, [validateClockInTime]);

  useEffect(() => {
    validateClockOutTime();
  }, [validateClockOutTime]);

  useEffect(() => {
    validateBreakSlots();
  }, [validateBreakSlots]);

  useEffect(() => {
    validateNursingBreaks();
  }, [validateNursingBreaks]);

  useEffect(() => {
    if (clockInTime && clockOutTime) {
      setCalculatedTimeWorked(calculateDuration(clockInTime, clockOutTime));
    } else {
      setCalculatedTimeWorked("0 hr 0 min");
    }
  }, [clockInTime, clockOutTime]);

  useEffect(() => {
    setCalculatedBreakDuration(calculateTotalDuration(breakSlots));
  }, [breakSlots]);

  useEffect(() => {
    setCalculatedNursingDuration(calculateTotalDuration(nursingSlots));
  }, [nursingSlots]);

  useEffect(() => {
    if (clockInTime && clockOutTime && originalClockInDate) {
      const clockInMinutes = timeToMinutes(clockInTime);
      const clockOutMinutes = timeToMinutes(clockOutTime);

      if (clockOutMinutes < clockInMinutes) {
        const nextDay = getDateWithoutTimeZone(rowData?.rawDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setClockOutDate(nextDay);
      } else {
        setClockOutDate(null);
      }
    } else if (!clockOutTime) {
      setClockOutDate(null);
    }
  }, [clockInTime, clockOutTime, originalClockInDate]);

  const getDaySummary = () => {
    const totalHours = clockInTime && clockOutTime ? calculateDuration(clockInTime, clockOutTime) : "0 hr 0 min";

    let timeWorked = "0 hr 0 min";
    if (clockInTime && clockOutTime) {
      const totalDuration = calculateDuration(clockInTime, clockOutTime);
      const breakDuration = calculatedBreakDuration || "0 min";
      const nursingDuration = calculatedNursingDuration || "0 min";

      const parseDurationToMinutes = (duration) => {
        if (!duration || duration === "0 min") return 0;
        const hrMatch = duration.match(/(\d+)\s*hr/);
        const minMatch = duration.match(/(\d+)\s*min/);
        const hours = hrMatch ? parseInt(hrMatch[1], 10) : 0;
        const minutes = minMatch ? parseInt(minMatch[1], 10) : 0;
        return hours * 60 + minutes;
      };

      const totalMinutes = parseDurationToMinutes(totalDuration);
      const breakMinutes = parseDurationToMinutes(breakDuration);
      const nursingMinutes = parseDurationToMinutes(nursingDuration);
      const workMinutes = Math.max(0, totalMinutes - breakMinutes - nursingMinutes);

      const workHours = Math.floor(workMinutes / 60);
      const workMins = workMinutes % 60;
      timeWorked = workHours > 0 ? `${workHours} hr ${workMins} min` : `${workMins} min`;
    }

    const breaks = calculatedBreakDuration || "0 min";
    const nursing = calculatedNursingDuration || "0 min";

    return {
      totalHours,
      timeWorked,
      breaks,
      nursing,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!open || !rowData) {
        return;
      }

      if (!employeeId) {
        setLoading(false);
        return;
      }

      if (!rowData?.timesheetId) {
        try {
          setLoading(true);
          const scheduleResponse = await getEmployeeSchedule(employeeId);
          if (scheduleResponse?.schedule) {
            const dayOfWeek = getDayOfWeekFromDate(rowData.rawDate || rowData.date);
            if (dayOfWeek !== null) {
              const daySchedule = getScheduleForDay(scheduleResponse.schedule, dayOfWeek);
              setCurrentDaySchedule(daySchedule);
            }
          }

          setClockInTime(null);
          setClockOutTime(null);
          setClockOutDate(null);
          setBreakSlots([{ id: 1, startTime: null, endTime: null, date: null }]);
          setNursingSlots([{ id: 1, startTime: null, endTime: null, date: null }]);

          setInitialClockInTime(null);
          setInitialClockOutTime(null);
          setInitialClockOutDate(null);
          setInitialBreakSlots([{ id: 1, startTime: null, endTime: null, date: null }]);
          setInitialNursingSlots([{ id: 1, startTime: null, endTime: null, date: null }]);

          setStartTimeError("");
          setEndTimeError("");
          setBreakSlotsError("");
          setNursingBreakError("");

          if (rowData?.rawDate) {
            const missedShiftDate = getDateWithoutTimeZone(rowData.rawDate);
            setOriginalClockInDate(missedShiftDate);
          }
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);

        let daySchedule = null;
        const scheduleResponse = await getEmployeeSchedule(employeeId);
        if (scheduleResponse?.schedule) {
          const dayOfWeek = getDayOfWeekFromDate(rowData.rawDate || rowData.date);
          if (dayOfWeek !== null) {
            daySchedule = getScheduleForDay(scheduleResponse.schedule, dayOfWeek);
            setCurrentDaySchedule(daySchedule);
          }
        }

        const response = await getTimesheetDetailsById(rowData.timesheetId);

        if (response?.success && response?.data) {
          let clockIn = null;
          let clockOut = null;
          let shiftDate = null;

          if (response.data.attendance) {
            const { attendance } = response.data;
            clockIn = parseDateTime(attendance.startTime);
            clockOut = parseDateTime(attendance.endTime);

            shiftDate = getDateWithoutTimeZone(attendance.date);
            setOriginalClockInDate(shiftDate);

            setClockInTime(clockIn);
            setClockOutTime(clockOut);
            setClockOutDate(null);

            setInitialClockInTime(clockIn);
            setInitialClockOutTime(clockOut);
            setInitialClockOutDate(null);
          }

          let breaks = [{ id: 1, startTime: null, endTime: null, date: null }];
          if (response.data.breaks && response.data.breaks.length > 0) {
            const mappedBreaks = response.data.breaks.map((breakItem, index) => {
              const startTime = parseDateTime(breakItem.startTime);
              const endTime = parseDateTime(breakItem.endTime);
              // Match v1: call with only startTime and endTime when loading data
              const nextDayInfo = getDateLabelForTime(startTime, endTime);

              return {
                id: index + 1,
                startTime,
                endTime,
                date: parseDateTime(breakItem.endTime),
                showNextDayLabel: nextDayInfo.showLabel,
                nextDayText: nextDayInfo.text,
              };
            });
            breaks = mappedBreaks;
          }
          setBreakSlots(breaks);
          setInitialBreakSlots(
            breaks.map((slot) => ({
              ...slot,
              startTime: slot.startTime ? new Date(slot.startTime) : null,
              endTime: slot.endTime ? new Date(slot.endTime) : null,
              date: slot.date ? new Date(slot.date) : null,
            }))
          );

          let nursing = [{ id: 1, startTime: null, endTime: null, date: null }];
          if (response.data.nursingBreaks && response.data.nursingBreaks.length > 0) {
            const mappedNursing = response.data.nursingBreaks.map((nursingItem, index) => {
              const startTime = parseDateTime(nursingItem.startTime);
              const endTime = parseDateTime(nursingItem.endTime);
              // Match v1: call with only startTime and endTime when loading data
              const nextDayInfo = getDateLabelForTime(startTime, endTime);

              return {
                id: index + 1,
                startTime,
                endTime,
                date: parseDateTime(nursingItem.endTime),
                showNextDayLabel: nextDayInfo.showLabel,
                nextDayText: nextDayInfo.text,
              };
            });
            nursing = mappedNursing;
          }
          setNursingSlots(nursing);
          setInitialNursingSlots(
            nursing.map((slot) => ({
              ...slot,
              startTime: slot.startTime ? new Date(slot.startTime) : null,
              endTime: slot.endTime ? new Date(slot.endTime) : null,
              date: slot.date ? new Date(slot.date) : null,
            }))
          );
        }
      } catch (error) {
        // already handled in modal logic
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowData?.timesheetId, rowData?.rawDate, employeeId]);

  const calculateTotalBreakDuration = (slots) => {
    let totalMinutes = 0;
    slots.forEach((slot) => {
      if (slot.startTime && slot.endTime) {
        const startMinutes = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
        const endMinutes = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

        let duration;
        if (endMinutes < startMinutes) {
          // Overnight break
          duration = 24 * 60 - startMinutes + endMinutes;
        } else {
          // Same day break
          duration = endMinutes - startMinutes;
        }

        totalMinutes += duration;
      }
    });
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    }
    return `${minutes} min`;
  };

  const handleBreakSlotChange = (slotId, field, value) => {
    setBreakSlots(
      breakSlots.map((slot) => {
        if (slot.id === slotId) {
          const updatedSlot = { ...slot, [field]: value };

          if (updatedSlot.startTime && updatedSlot.endTime) {
            const nextDayInfo = getDateLabelForTime(
              updatedSlot.startTime,
              updatedSlot.endTime,
              originalClockInDate,
              currentDaySchedule?.startTime,
              currentDaySchedule?.endTime
            );
            updatedSlot.showNextDayLabel = nextDayInfo.showLabel;
            updatedSlot.nextDayText = nextDayInfo.text;
          }

          return updatedSlot;
        }
        return slot;
      })
    );
  };

  const handleAddBreakSlot = () => {
    setBreakSlots((prevSlots) => [
      ...prevSlots,
      { id: prevSlots.length + 1, startTime: null, endTime: null, date: null },
    ]);
  };

  const handleRemoveBreakSlot = (id) => {
    if (breakSlots.length > 1) {
      setBreakSlots((prevSlots) => prevSlots.filter((slot) => slot.id !== id));
    }
  };

  const handleNursingSlotChange = (slotId, field, value) => {
    setNursingSlots(
      nursingSlots.map((slot) => {
        if (slot.id === slotId) {
          const updatedSlot = { ...slot, [field]: value };

          if (updatedSlot.startTime && updatedSlot.endTime) {
            const nextDayInfo = getDateLabelForTime(
              updatedSlot.startTime,
              updatedSlot.endTime,
              originalClockInDate,
              currentDaySchedule?.startTime,
              currentDaySchedule?.endTime
            );
            updatedSlot.showNextDayLabel = nextDayInfo.showLabel;
            updatedSlot.nextDayText = nextDayInfo.text;
          }

          return updatedSlot;
        }
        return slot;
      })
    );
  };

  const handleAddNursingSlot = () => {
    setNursingSlots((prevSlots) => [
      ...prevSlots,
      { id: prevSlots.length + 1, startTime: null, endTime: null, date: null },
    ]);
  };

  const handleRemoveNursingSlot = (id) => {
    if (nursingSlots.length > 1) {
      setNursingSlots((prevSlots) => prevSlots.filter((slot) => slot.id !== id));
    }
  };

  // PRD-2037: on an overnight shift every time after midnight belongs to the following calendar
  // day. Resolving that date lives in the helper so the payload agrees with the form validation.
  const formatDateTimeForPayload = (time, includeDate = false, isClockIn = false) =>
    formatTimesheetDateTimeForPayload(time, {
      baseDate: getDateWithoutTimeZone(rowData?.rawDate),
      clockInTime,
      scheduleStart: currentDaySchedule?.startTime,
      scheduleEnd: currentDaySchedule?.endTime,
      isClockIn,
      includeDate,
    });

  const validateForm = () => {
    const isClockInValid = validateClockInTime();
    const isClockOutValid = validateClockOutTime();
    const areBreaksValid = validateBreakSlots();
    const areNursingBreaksValid = validateNursingBreaks();

    return isClockInValid && isClockOutValid && areBreaksValid && areNursingBreaksValid;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (!clockInTime) {
      setStartTimeError("Clock in time is required");
      return;
    }

    setShowEndDayModal(true);
  };

  const resetFields = () => {
    setClockInTime(initialClockInTime);
    setClockOutTime(initialClockOutTime);
    setClockOutDate(initialClockOutDate);
    setBreakSlots(
      initialBreakSlots.map((slot) => ({
        ...slot,
        startTime: slot.startTime ? new Date(slot.startTime) : null,
        endTime: slot.endTime ? new Date(slot.endTime) : null,
        date: slot.date ? new Date(slot.date) : null,
      }))
    );
    setNursingSlots(
      initialNursingSlots.map((slot) => ({
        ...slot,
        startTime: slot.startTime ? new Date(slot.startTime) : null,
        endTime: slot.endTime ? new Date(slot.endTime) : null,
        date: slot.date ? new Date(slot.date) : null,
      }))
    );

    setStartTimeError("");
    setEndTimeError("");
    setBreakSlotsError("");
    setNursingBreakError("");
  };

  const handleCancel = () => {
    resetFields();
    onClose();
  };

  const handleEndDayConfirm = async () => {
    try {
      setSaving(true);
      const payload = {
        employeeId,
        shiftDate: rowData?.rawDate,
        attendance: {
          startTime: formatDateTimeForPayload(clockInTime, true, true),
          endTime: formatDateTimeForPayload(clockOutTime, true),
        },
        breaks: breakSlots
          .filter((slot) => slot.startTime && slot.endTime)
          .map((slot) => ({
            startTime: formatDateTimeForPayload(slot.startTime, true),
            endTime: formatDateTimeForPayload(slot.endTime, true),
          })),
        nursingBreaks: nursingSlots
          .filter((slot) => slot.startTime && slot.endTime)
          .map((slot) => ({
            startTime: formatDateTimeForPayload(slot.startTime, true),
            endTime: formatDateTimeForPayload(slot.endTime, true),
          })),
      };

      const response = await updateTimeSheetById(rowData.timesheetId, payload, "v2/");
      setSaving(false);
      setShowEndDayModal(false);

      if (response?.payload?.success) {
        setIsSuccess(true);
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
      } else {
        setIsSuccess(false);
      }

      resetFields();
      setShowResultModal(true);
    } catch (error) {
      setSaving(false);
      setShowEndDayModal(false);
      setIsSuccess(false);
      resetFields();
      setShowResultModal(true);
    }
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    onClose();
  };

  const shiftTime = currentDaySchedule
    ? formatShiftTime(currentDaySchedule.startTime, currentDaySchedule.endTime)
    : "N/A";

  const timeWorked = calculatedTimeWorked;
  const breakDuration = calculatedBreakDuration;
  const nursingBreakDuration = calculatedNursingDuration;

  const clockOutLabel = getDateLabelForTime(
    clockInTime,
    clockOutTime,
    originalClockInDate,
    currentDaySchedule?.startTime,
    currentDaySchedule?.endTime
  );

  const getDialogActions = () => {
    if (loading) {
      return null;
    }

    if (!canEdit) {
      return (
        <DialogActions
          action2={<CustomButton label="Done" variant="contained" color="primary" onClick={handleCancel} />}
        />
      );
    }

    return (
      <DialogActions
        action1={<CustomButton label="Cancel" variant="outlined" color="secondary" onClick={handleCancel} />}
        action2={
          <CustomButton
            label="Save"
            size="medium"
            variant="contained"
            color="primary"
            onClick={handleSave}
            isDisabled={!!startTimeError || !!endTimeError || !!breakSlotsError || !!nursingBreakError || !clockInTime}
          />
        }
      />
    );
  };
  return (
    <>
      <Dialog
        open={open && !showEndDayModal && !showResultModal}
        onClose={handleCancel}
        size="md"
        title={<DialogTitle title={rowData?.date || "Timesheet Details"} />}
        content={
          <DialogContent>
            {loading ? (
              <PageLoader />
            ) : (
              <div className="timesheet-detail-content">
                {/* Summary Cards */}
                <div className="timesheet-summary-grid">
                  <div className="summary-card">
                    <p className="summary-label">Shift time (local time)</p>
                    <div className="summary-value">{shiftTime}</div>
                  </div>
                  <div className="summary-card">
                    <p className="summary-label">Time worked</p>
                    <div className="summary-value">{timeWorked}</div>
                  </div>
                  <div className="summary-card">
                    <p className="summary-label">Break duration</p>
                    <div className="summary-value">{breakDuration}</div>
                  </div>
                  <div className="summary-card">
                    <p className="summary-label">Time spent nursing</p>
                    <div className="summary-value">{nursingBreakDuration}</div>
                  </div>
                </div>

                <TimeRangeSelector
                  startTime={clockInTime}
                  endTime={clockOutTime}
                  onStartTimeChange={setClockInTime}
                  onEndTimeChange={setClockOutTime}
                  startLabel="Clock in"
                  endLabel="Clock out"
                  helperText=""
                  showArrow
                  dateValue={clockOutDate}
                  onDateChange={setClockOutDate}
                  dateLabel="Date"
                  disabled={!canEdit}
                  endTimeDisabled={!canEdit || !isEdgeAdmin}
                  showHeading
                  heading="Clock in & Clock out"
                  totalDuration={calculateDuration(clockInTime, clockOutTime)}
                  showNextDayLabel={clockOutLabel.showLabel}
                  nextDayText={clockOutLabel.text}
                  nextDayLabel="Date"
                  startTimeError={startTimeError}
                  endTimeError={endTimeError}
                  arrowClassName="time-range-selector-arrow"
                  timeRangeSelectorContainerClassName="time-range-selector-container"
                />

                <TimeRangeSelector
                  multipleSlots
                  slots={breakSlots}
                  onSlotChange={handleBreakSlotChange}
                  onAddSlot={handleAddBreakSlot}
                  onRemoveSlot={handleRemoveBreakSlot}
                  startLabel="Start time"
                  endLabel="End time"
                  helperText=""
                  showArrow
                  dateLabel="Date"
                  disabled={!canEdit}
                  showHeading
                  heading="Break time"
                  totalDuration={calculateTotalBreakDuration(breakSlots)}
                  addSlotButtonText="Add break slot"
                  nextDayLabel="Date"
                  breakSlotsError={breakSlotsError}
                  arrowClassName="time-range-selector-arrow"
                  timeRangeSelectorContainerClassName="time-range-selector-container"
                />

                <TimeRangeSelector
                  multipleSlots
                  slots={nursingSlots}
                  onSlotChange={handleNursingSlotChange}
                  onAddSlot={handleAddNursingSlot}
                  onRemoveSlot={handleRemoveNursingSlot}
                  startLabel="Start time"
                  endLabel="End time"
                  helperText=""
                  showArrow
                  dateLabel="Date"
                  disabled={!canEdit}
                  showHeading
                  heading="Nursing break"
                  totalDuration={`${calculateTotalBreakDuration(nursingSlots)} (1 hr max)`}
                  addSlotButtonText="Add nursing slot"
                  nextDayLabel="Date"
                  breakSlotsError={nursingBreakError}
                  arrowClassName="time-range-selector-arrow"
                  timeRangeSelectorContainerClassName="time-range-selector-container"
                />
              </div>
            )}
          </DialogContent>
        }
        actions={getDialogActions()}
      />

      <EndDayModal
        title="Save changes to your day"
        description="Review your updated summary below. These changes will be reflected in your timesheet immediately."
        confirmLabel="Save"
        open={showEndDayModal}
        onClose={() => setShowEndDayModal(false)}
        onConfirm={handleEndDayConfirm}
        daySummary={getDaySummary()}
        employeeName={employeeName}
        date={rowData?.date || ""}
        isLoading={saving}
      />

      <TimesheetResultModal open={showResultModal} onClose={handleResultModalClose} isSuccess={isSuccess} />
    </>
  );
}

TimesheetDetailModalV2.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.shape({
    id: PropTypes.number,
    timesheetId: PropTypes.number,
    date: PropTypes.string,
    rawDate: PropTypes.string,
    status: PropTypes.string,
    clockedIn: PropTypes.string,
    clockedOut: PropTypes.string,
    timeWorked: PropTypes.string,
    timeWorkedSeconds: PropTypes.number,
    breakDuration: PropTypes.string,
    breakSeconds: PropTypes.number,
    nursing: PropTypes.string,
    nursingBreakSeconds: PropTypes.number,
  }),
  isEditable: PropTypes.bool,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdateSuccess: PropTypes.func,
  employeeDetails: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
};

TimesheetDetailModalV2.defaultProps = {
  rowData: null,
  isEditable: true,
  employeeId: null,
  onUpdateSuccess: null,
  employeeDetails: {},
};

export default TimesheetDetailModalV2;
